import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Alert,
  Box,
  Grid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { AnchorLink } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";

import { api } from "@/api.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/customers/$id")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Customer`,
      },
    ],
  }),
});

function RouteComponent() {
  const { data } = useSuspenseQueryPostgrest(
    api
      .from("customer")
      .select("*,project(identifier,name,customer_domain)")
      .eq("id", parseInt(Route.useParams().id))
      .single(),
  );

  const { t } = useLingui();

  const customerProject = data.project.find((p) => p.customer_domain);

  return (
    <Grid>
      <Grid.Col span={7}>
        <Title order={3}>
          <Trans>Projects</Trans>
        </Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Trans>Identifier</Trans>
              </Table.Th>
              <Table.Th>
                <Trans>Name</Trans>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.project.map((p) => (
              <Table.Tr key={p.identifier}>
                <Table.Td>{p.identifier}</Table.Td>
                <Table.Td>{p.name}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Grid.Col>
      <Grid.Col span={5}>
        <Title order={3}>
          <Trans>Access & Cost</Trans>
        </Title>
        <Title order={4}>
          <Trans>Sweco Users</Trans>
        </Title>
        <Text>
          <Trans>
            Usage by Sweco users are charged to the selected project. Because of
            contractual terms we may have to pay for data, even for customers
            that are a member of Geodatasamverkan.
          </Trans>
        </Text>

        <Title order={4}>
          <Trans>Customer Users</Trans>
        </Title>
        {customerProject ? (
          <Text>
            <Trans>Users for this customer have access to REALity.</Trans>{" "}
            {data.is_geodatasamverkan_member ? (
              <Trans>
                Because they are a member of Geodatasamverkan they only pay for
                the service, not the data.
              </Trans>
            ) : (
              <Trans>
                Because they are not a member of Geodatasamverkan they pay for
                both the service and the data.
              </Trans>
            )}{" "}
            <Trans>
              Their usage will be charged to{" "}
              <AnchorLink
                to="/projects/$identifier"
                params={{ identifier: customerProject.identifier }}
              >
                {customerProject.identifier} ({customerProject.name})
              </AnchorLink>
              .
            </Trans>
          </Text>
        ) : (
          <Alert color="alert">
            <Trans>Users for this customer do not have access to REALity</Trans>
          </Alert>
        )}

        <Box bg="gray" w="100%" h="1px" my="md" />

        <Title order={3}>
          <Trans>Settings</Trans>
        </Title>
        <Stack>
          <TextInput label={t`Name`} value={data.name} readOnly disabled />
          <TextInput
            label={t`Organisation Number`}
            value={data.organisation_number}
            readOnly
            disabled
          />
          <Switch
            label={t`Is Geodatasamverkan Member`}
            checked={data.is_geodatasamverkan_member}
            readOnly
            disabled
          />
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
